import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, AlertCircle, Check } from 'lucide-react';
import { useSiteContext } from '../contexts/SiteContext';

const AdminSuggestions = ({ user }) => {
  const { selectedSite, sites } = useSiteContext();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formSite, setFormSite] = useState(selectedSite?.id || '');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    recommendation: '',
    priority: 'medium'
  });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (formSite) {
      fetchSuggestions(formSite);
    }
  }, [formSite, user]);

  const fetchSuggestions = async (siteId) => {
    if (!siteId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/app/api/admin/suggestions-manage.php?site_id=${siteId}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuggestions(result.data || []);
      } else {
        setError(result.message || 'Failed to fetch suggestions');
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingId(null);
    setFormData({ title: '', description: '', recommendation: '', priority: 'medium' });
    setShowForm(true);
  };

  const handleEdit = (suggestion) => {
    setEditingId(suggestion.id);
    setFormData({
      title: suggestion.title,
      description: suggestion.description,
      recommendation: suggestion.recommendation || '',
      priority: suggestion.priority || 'medium'
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description) {
      setError('Title and description are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const method = editingId ? 'PUT' : 'POST';
      const body = {
        site_id: formSite,
        title: formData.title,
        description: formData.description,
        recommendation: formData.recommendation,
        priority: formData.priority
      };

      if (editingId) {
        body.id = editingId;
      }

      const response = await fetch('/app/api/admin/suggestions-manage.php', {
        method,
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setShowForm(false);
        await fetchSuggestions(formSite);
      } else {
        setError(result.message || 'Failed to save suggestion');
      }
    } catch (error) {
      console.error('Error saving suggestion:', error);
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (suggestionId) => {
    setSaving(true);
    setError(null);

    try {
      const response = await fetch('/app/api/admin/suggestions-manage.php', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: suggestionId })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setDeleteConfirm(null);
        await fetchSuggestions(formSite);
      } else {
        setError(result.message || 'Failed to delete suggestion');
      }
    } catch (error) {
      console.error('Error deleting suggestion:', error);
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Manage AI Suggestions</h1>
            <p className="text-gray-600">Add site-specific suggestions for users to see</p>
          </div>
          <button
            onClick={handleAddNew}
            disabled={!formSite}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
          >
            <Plus className="w-5 h-5" />
            Add Suggestion
          </button>
        </div>

        {/* Site Selector */}
        <div className="mb-6 bg-white rounded-lg shadow-md p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Site
          </label>
          <select
            value={formSite}
            onChange={(e) => setFormSite(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">-- Select a site --</option>
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <div className="mb-6 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {editingId ? 'Edit Suggestion' : 'Add New Suggestion'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Improve Product Descriptions"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Explain what this suggestion is about..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recommendation
                </label>
                <textarea
                  value={formData.recommendation}
                  onChange={(e) => setFormData({ ...formData, recommendation: e.target.value })}
                  rows="2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="What action should the user take? (Optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                >
                  <Check className="w-5 h-5" />
                  {saving ? 'Saving...' : 'Save Suggestion'}
                </button>
                <button
                  onClick={() => setShowForm(false)}
                  disabled={saving}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Suggestions List */}
        {formSite && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {loading ? (
              <div className="p-8 flex items-center justify-center">
                <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mr-3" />
                <span className="text-gray-600">Loading suggestions...</span>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">No suggestions added for this site yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {suggestions.map((suggestion) => (
                  <div key={suggestion.id} className="p-6 hover:bg-gray-50 transition">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-800">{suggestion.title}</h3>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              suggestion.priority === 'high'
                                ? 'bg-red-100 text-red-700'
                                : suggestion.priority === 'medium'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-green-100 text-green-700'
                            }`}
                          >
                            {suggestion.priority}
                          </span>
                        </div>
                        <p className="text-gray-600 mt-1">{suggestion.description}</p>
                        {suggestion.recommendation && (
                          <p className="text-sm text-gray-500 mt-2">
                            <span className="font-semibold">Recommendation:</span> {suggestion.recommendation}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleEdit(suggestion)}
                          disabled={saving}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:text-gray-400 disabled:cursor-not-allowed transition"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(suggestion.id)}
                          disabled={saving}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:text-gray-400 disabled:cursor-not-allowed transition"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Delete Confirmation */}
                    {deleteConfirm === suggestion.id && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded flex items-center justify-between">
                        <p className="text-sm text-red-800">Are you sure? This cannot be undone.</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDelete(suggestion.id)}
                            disabled={saving}
                            className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                          >
                            Delete
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            disabled={saving}
                            className="px-3 py-1 bg-gray-300 text-gray-800 rounded text-sm hover:bg-gray-400 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSuggestions;
